<template>
  <div class="h-screen flex flex-col overflow-hidden">
    <div class="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
      <!-- Section Filter -->
      <SectionFilter
        title="Sanity Requests"
        :show-search="false"
        :active-filter-count="activeFiltersCount"
        :has-active-filters="activeFiltersCount > 0"
        :show-export="false"
        :show-filters="true"
        :expanded="showFilters"
        :filtered-count="filteredRequests.length"
        :total-count="showDeleted ? sanityRequests.length : sanityRequests.filter(r => !r.is_deleted).length"
        show-deleted-toggle
        :include-deleted="showDeleted"
        @toggle="showFilters = !showFilters"
        @clear="clearAllFilters"
        @update:include-deleted="showDeleted = $event"
      >
        <template #actions="{ defaults }">
          <HLButton
            v-bind="defaults"
            @click="showDeleted = !showDeleted"
            variant="secondary"
            color="gray"
            :aria-pressed="showDeleted"
          >
            <template #iconLeft>
              <Icon name="archive" size="sm" />
            </template>
            {{ showDeleted ? 'Hide Deleted' : 'View Deleted' }}
          </HLButton>

          <HLButton
            v-bind="defaults"
            variant="primary"
            color="blue"
            @click="createAndNavigate"
            :loading="creating"
          >
            <template #iconLeft>
              <Icon name="plus" size="sm" />
            </template>
            Cross Team Testing Request
          </HLButton>
        </template>

        <template #default>
          <!-- Search Bar -->
          <div class="col-span-full mb-4">
            <HLInput
              v-model="filters.search"
              placeholder="Search by title or description..."
              clearable
              class="w-full"
            >
              <template #prefix>
                <Icon name="search" size="sm" class="text-gray-400" />
              </template>
            </HLInput>
          </div>

          <!-- Filter Grid -->
          <div class="col-span-full">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Status Filter -->
              <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Status</HLText>
                <HLSelect
                  v-model:value="filters.status"
                  :options="statusFilterOptions"
                  placeholder="All Statuses"
                  multiple
                  filterable
                />
              </div>

              <!-- Team Filter -->
              <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Team</HLText>
                <HLSelect
                  v-model:value="filters.teamIds"
                  :options="teamFilterOptions"
                  placeholder="All Teams"
                  multiple
                  filterable
                />
              </div>

              <!-- Environment Filter -->
              <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Environment</HLText>
                <HLSelect
                  v-model:value="filters.environment"
                  :options="environmentFilterOptions"
                  placeholder="All Environments"
                  multiple
                  filterable
                />
              </div>

              <!-- Created By Filter -->
              <div>
                <HLText as="label" variant="label" class="block mb-1 font-bold text-base">Created By</HLText>
                <HLSelect
                  v-model:value="filters.createdBy"
                  :options="createdByFilterOptions"
                  placeholder="All Users"
                  multiple
                  filterable
                />
              </div>
            </div>
          </div>
        </template>
      </SectionFilter>

      <!-- Sanity Requests Table -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-4">
        <!-- Loading State -->
        <div v-if="loading" class="p-12 flex justify-center">
          <HLSpin size="lg" :show="loading">
            <template #description>
              <HLText variant="body-sm" color="muted">Loading sanity requests...</HLText>
            </template>
          </HLSpin>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredRequests.length === 0" class="p-12">
          <HLEmpty
            description="No sanity requests found"
            class="py-12"
          >
            <template #image>
              <HLIcon size="48" color="#9CA3AF">
                <Users01Icon />
              </HLIcon>
            </template>
          </HLEmpty>
        </div>

        <!-- Table -->
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Team
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Environment
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ETA
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created By
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created At
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="request in filteredRequests"
                :key="request.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <!-- ID -->
                <td 
                  class="px-6 py-4 whitespace-nowrap cursor-pointer"
                  @click="viewRequest(request)"
                >
                  <div class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                    {{ formatSanityId(request.sanity_id || request.id) }}
                  </div>
                </td>

                <!-- Title -->
                <td 
                  class="px-6 py-4 whitespace-nowrap cursor-pointer"
                  @click="viewRequest(request)"
                >
                  <div class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                    {{ request.name || 'Untitled Request' }}
                  </div>
                  <div v-if="request.description" class="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-md">
                    {{ request.description }}
                  </div>
                </td>

                <!-- Status -->
                <td class="px-6 py-4 whitespace-nowrap" @click.stop>
                  <div class="flex items-center space-x-2">
                    <HLTag
                      :color="getStatusColor(request.calculatedStatus || request.status)"
                      size="sm"
                      round
                    >
                      {{ getStatusLabel(request.calculatedStatus || request.status) }}
                    </HLTag>
                    <HLTag
                      v-if="request.is_deleted"
                      color="red"
                      size="xs"
                    >
                      Deleted
                    </HLTag>
                  </div>
                </td>

                <!-- Teams -->
                <td class="px-6 py-4">
                  <div class="flex flex-wrap gap-1">
                    <HLTag
                      v-for="team in getRequestTeams(request)"
                      :key="team"
                      color="blue"
                      size="xs"
                      class="mb-1"
                    >
                      {{ team }}
                    </HLTag>
                    <span v-if="!getRequestTeams(request).length" class="text-sm text-gray-500 dark:text-gray-400">
                      No teams
                    </span>
                  </div>
                </td>

                <!-- Environment -->
                <td class="px-6 py-4">
                  <div class="flex flex-wrap gap-1">
                    <HLTag
                      v-for="env in (request.environments || [])"
                      :key="env"
                      color="purple"
                      size="xs"
                    >
                      {{ getEnvironmentLabel(env) }}
                    </HLTag>
                    <span v-if="!request.environments || request.environments.length === 0" class="text-sm text-gray-500 dark:text-gray-400">
                      Not set
                    </span>
                  </div>
                </td>

                <!-- ETA -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center space-x-2">
                    <span class="text-sm text-gray-900 dark:text-white">
                      {{ request.eta ? formatDate(request.eta) : 'Not set' }}
                    </span>
                    <HLIcon
                      v-if="isOverdue(request.eta)"
                      size="16"
                      color="#F59E0B"
                      class="flex-shrink-0"
                    >
                      <AlertTriangleIcon />
                    </HLIcon>
                  </div>
                </td>

                <!-- Created By -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900 dark:text-white">
                    {{ request.created_by_name || 'Unknown' }}
                  </div>
                </td>

                <!-- Created At -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    {{ formatDate(request.created_at) }}
                  </div>
                </td>

                <!-- Actions -->
                <td class="px-6 py-4 whitespace-nowrap text-right" @click.stop>
                  <div class="flex items-center justify-end space-x-2">
                    <!-- Restore Button (for deleted items) -->
                    <HLButton
                      v-if="request.is_deleted"
                      variant="ghost"
                      size="xs"
                      @click.stop="restoreRequest(request)"
                    >
                      <template #iconLeft>
                        <HLIcon size="16" color="#10B981">
                          <RefreshCw01Icon />
                        </HLIcon>
                      </template>
                    </HLButton>
                    
                    <!-- Delete Button (for non-deleted items) -->
                    <HLButton
                      v-else
                      variant="ghost"
                      size="xs"
                      @click.stop="deleteRequest(request)"
                    >
                      <template #iconLeft>
                        <HLIcon size="16" color="#EF4444">
                          <Trash01Icon />
                        </HLIcon>
                      </template>
                    </HLButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteSanityRequestModal
      :show="showDeleteModal"
      :request-name="requestToDelete?.name || ''"
      @update:show="showDeleteModal = $event"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import {
  HLButton,
  HLIcon,
  HLEmpty,
  HLInput,
  HLSelect,
  HLTag,
  HLSpin,
  HLText
} from '@platform-ui/highrise'
import {
  Users01Icon,
  AlertTriangleIcon,
  Trash01Icon,
  RefreshCw01Icon
} from '@gohighlevel/ghl-icons/24/outline'
import Icon from '@/components/ui/Icon.vue'
import SectionFilter from '@/components/reports/SectionFilter.vue'
import { useToast } from '@/composables/useToast'
import { useAuthStore } from '@/stores/auth'
import { crossTeamSanityService, teamService, userService, testRunService } from '@/database/services'
import DeleteSanityRequestModal from '@/components/modals/DeleteSanityRequestModal.vue'

const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()

// Reactive data
const loading = ref(false)
const creating = ref(false)
const showFilters = ref(false)
const showDeleted = ref(false)
const sanityRequests = ref([])
const teams = ref([])
const users = ref([])
const showDeleteModal = ref(false)
const requestToDelete = ref(null)

// Filters
const filters = ref({
  search: '',
  status: [],
  teamIds: [],
  environment: [],
  createdBy: []
})

// Computed
const statusOptions = [
  { label: 'Not Started', value: 'active' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Blocked', value: 'blocked' }
]

const statusFilterOptions = computed(() => statusOptions)

const teamFilterOptions = computed(() => {
  return teams.value.map(team => ({
    label: team.name,
    value: team.id
  }))
})

const environmentFilterOptions = [
  { label: 'Development', value: 'development' },
  { label: 'Versioned Staging', value: 'versioned_staging' },
  { label: 'Default Staging', value: 'default_staging' },
  { label: 'Interop', value: 'interop' },
  { label: 'Beta', value: 'beta' },
  { label: 'Production', value: 'production' }
]

const createdByFilterOptions = computed(() => {
  return users.value.map(user => ({
    label: user.name || user.email,
    value: user.id
  }))
})

const activeFiltersCount = computed(() => {
  let count = 0
  if (filters.value.search) count++
  if (filters.value.status.length > 0) count++
  if (filters.value.teamIds.length > 0) count++
  if (filters.value.environment.length > 0) count++
  if (filters.value.createdBy.length > 0) count++
  return count
})

const filteredRequests = computed(() => {
  let filtered = sanityRequests.value

  // Filter by deleted status
  if (!showDeleted.value) {
    filtered = filtered.filter(r => !r.is_deleted)
  }

  // Search filter
  if (filters.value.search.trim()) {
    const query = filters.value.search.toLowerCase()
    filtered = filtered.filter(request =>
      request.name?.toLowerCase().includes(query) ||
      request.description?.toLowerCase().includes(query)
    )
  }

  // Status filter
  if (filters.value.status.length > 0) {
    filtered = filtered.filter(request => {
      const status = request.calculatedStatus || request.status
      return filters.value.status.includes(status)
    })
  }

  // Team filter
  if (filters.value.teamIds.length > 0) {
    filtered = filtered.filter(request =>
      request.teams?.some(team => filters.value.teamIds.includes(team.teamId))
    )
  }

  // Environment filter
  if (filters.value.environment.length > 0) {
    filtered = filtered.filter(request =>
      request.environments?.some(env => filters.value.environment.includes(env))
    )
  }

  // Created By filter
  if (filters.value.createdBy.length > 0) {
    filtered = filtered.filter(request =>
      filters.value.createdBy.includes(request.created_by)
    )
  }

  return filtered
})

// Methods
const calculateDynamicStatus = (request, allTestRuns) => {
  // If manually set to cancelled, keep it cancelled
  if (request.status === 'cancelled') {
    return 'cancelled'
  }
  
  // Check if any form fields have been filled (indicates work has started)
  const hasFormData = 
    (request.description && request.description.trim().length > 0) ||
    (request.environments && request.environments.length > 0) ||
    (request.sanityType && request.sanityType.trim().length > 0) ||
    (request.eta) ||
    (request.urlToValidate && request.urlToValidate.trim().length > 0) ||
    (request.ticketLink && request.ticketLink.trim().length > 0) ||
    (request.prLinks && request.prLinks.trim().length > 0) ||
    (request.executionNotes && request.executionNotes.trim().length > 0) ||
    (request.teams && request.teams.length > 0)
  
  // If no teams added and no form data, return 'active' (not started)
  if (!request.teams || request.teams.length === 0) {
    return hasFormData ? 'in_progress' : 'active'
  }
  
  try {
    let totalProgress = 0
    let totalTestRuns = 0
    let hasBlocked = false
    let allCompleted = true
    
    // Calculate overall progress from all test runs
    request.teams.forEach(team => {
      (team.projects || []).forEach(project => {
        (project.testRuns || []).forEach(testRunRef => {
          const actualTestRun = allTestRuns.find(tr => tr.id === testRunRef.testRunId && !tr.is_deleted)
          if (actualTestRun) {
            const testCases = actualTestRun.test_cases || []
            if (testCases.length > 0) {
              // Only count 'passed' test cases towards progress
              const passed = testCases.filter(tc => tc.status === 'passed').length
              const progress = Math.round((passed / testCases.length) * 100)
              
              totalProgress += progress
              totalTestRuns++
              
              // Check if blocked
              if (actualTestRun.status === 'blocked' || actualTestRun.status === 'CANCELLED') {
                hasBlocked = true
              }
              
              // Check if all completed (100% progress)
              if (progress < 100) {
                allCompleted = false
              }
            }
            // Note: Test runs with no test cases are ignored (not counted as incomplete or towards total)
            // This is intentional as the system creates empty test runs when projects lack test cases for the sanity type
          }
        })
      })
    })
    
    // Calculate status based on progress
    if (hasBlocked) {
      return 'blocked'
    }
    
    if (allCompleted && totalTestRuns > 0) {
      return 'completed' // All test runs are 100% (all test cases passed)
    }
    
    // If there are test runs but no progress, or if form data exists, it's in progress
    if (totalTestRuns > 0 || hasFormData) {
      const avgProgress = totalTestRuns > 0 ? Math.round(totalProgress / totalTestRuns) : 0
      if (avgProgress > 0 || hasFormData) {
        return 'in_progress' // Some progress made or form has been filled
      }
    }
    
    return 'in_progress' // Form has been filled, so it's in progress
  } catch (error) {
    // If form has data, return in_progress, otherwise return stored status or active
    return hasFormData ? 'in_progress' : (request.status || 'active')
  }
}

const loadData = async () => {
  loading.value = true
  try {
    // Load all test runs once (for performance)
    let allTestRuns = []
    try {
      allTestRuns = await testRunService.getAll()
    } catch (testRunError) {
      // Continue without test runs - status calculations will use default values
    }
    
    // Load all sanity requests (including deleted for restore functionality)
    const requests = await crossTeamSanityService.getAll()
    
    // Calculate dynamic status for each request
    requests.forEach(request => {
      if (!request.is_deleted) {
        request.calculatedStatus = calculateDynamicStatus(request, allTestRuns)
      } else {
        request.calculatedStatus = request.status // Keep original status for deleted items
      }
    })
    
    sanityRequests.value = requests

    // Load teams for dropdown
    let allTeams = []
    try {
      allTeams = await teamService.getAll()
    teams.value = allTeams.filter(t => !t.is_deleted)
    } catch (teamError) {
      teams.value = []
    }

    // Load users for created by filter
    let allUsers = []
    try {
      allUsers = await userService.getAll()
    users.value = allUsers.filter(u => !u.is_deleted)
    } catch (userError) {
      users.value = []
    }
  } catch (error) {
    toast.error(`Failed to load sanity requests: ${error.message || 'Unknown error'}`)
  } finally {
    loading.value = false
  }
}

const clearAllFilters = () => {
  filters.value = {
    search: '',
    status: [],
    teamIds: [],
    environment: [],
    createdBy: []
  }
}

const getStatusColor = (status) => {
  const colors = {
    'active': 'gray', // Not Started
    'in_progress': 'blue', // In Progress
    'completed': 'green', // Completed
    'cancelled': 'gray',
    'blocked': 'red'
  }
  return colors[status] || 'gray'
}

const getStatusLabel = (status) => {
  const labels = {
    'active': 'Not Started',
    'in_progress': 'In Progress',
    'completed': 'Completed',
    'cancelled': 'Cancelled',
    'blocked': 'Blocked'
  }
  return labels[status] || status
}

const getEnvironmentLabel = (environment) => {
  const labels = {
    'development': 'Development',
    'versioned_staging': 'Versioned Staging',
    'default_staging': 'Default Staging',
    'interop': 'Interop',
    'beta': 'Beta',
    'production': 'Production'
  }
  return labels[environment] || environment
}

const formatSanityId = (id) => {
  if (!id) return '—'
  
  // Extract number from formats like "cross_team_testing_00031" or "SAN123"
  const match = id.match(/(?:cross_team_testing_|SAN)(\d+)$/)
  if (match) {
    const number = match[1]
    // Format as CT##### (Cross Team) similar to TR#####
    return `CT${number.padStart(5, '0')}`
  }
  
  // If it doesn't match expected format, try to extract any trailing number
  const numberMatch = id.match(/(\d+)$/)
  if (numberMatch) {
    return `CT${numberMatch[1].padStart(5, '0')}`
  }
  
  // Fallback: return original ID if no number found
  return id
}

const getRequestTeams = (request) => {
  // Get unique team names (since each team can have multiple projects)
  const teamNames = new Set()
  ;(request.teams || []).forEach(team => {
    if (team.teamName) teamNames.add(team.teamName)
  })
  return Array.from(teamNames)
}

const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const isOverdue = (eta) => {
  if (!eta) return false
  return new Date(eta) < new Date()
}

const viewRequest = (request) => {
  router.push(`/cross-team-sanity/${request.id}`)
}

const deleteRequest = (request) => {
  requestToDelete.value = request
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (!requestToDelete.value) return
  
  try {
    const currentUser = authStore.userProfile || authStore.user
    await crossTeamSanityService.softDelete(
      requestToDelete.value.id,
      currentUser?.id || currentUser?.uid
    )
    
    toast.success('Sanity request deleted successfully')
    showDeleteModal.value = false
    requestToDelete.value = null
    
    // Reload data
    loadData()
  } catch (error) {
    toast.error('Failed to delete sanity request')
  }
}

const restoreRequest = async (request) => {
  try {
    await crossTeamSanityService.update(request.id, {
      is_deleted: false,
      deleted_at: null,
      deleted_by: null
    })
    
    toast.success('Sanity request restored successfully')
    
    // Reload data
    loadData()
  } catch (error) {
    toast.error('Failed to restore sanity request')
  }
}

const updateRequestStatus = async (request, newStatus) => {
  try {
    await crossTeamSanityService.update(request.id, {
      status: newStatus
    })
    
    // Update local state
    const index = sanityRequests.value.findIndex(r => r.id === request.id)
    if (index > -1) {
      sanityRequests.value[index].status = newStatus
    }
    
    toast.success('Status updated successfully')
  } catch (error) {
    toast.error('Failed to update status')
  }
}

const createAndNavigate = async () => {
  // Navigate to detail page in "create new" mode without creating database record
  // Record will be created when user clicks "Create Test Run" button
  router.push('/cross-team-sanity/new')
}

// Lifecycle
onMounted(() => {
  loadData()
})
</script>
